import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Ban, Undo2, FileDown, Printer, Lock } from "lucide-react";
import { RetireDialog } from "@/components/RecordEditor";
import RejectionDialog from "@/components/RejectionDialog";
import { ModuleNav } from "./ModuleNav";

const download = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
};

const EMPTY_RESOLVE = {
  resolution_type: "", contacted_name: "", contacted_role: "", contacted_at: "",
  contacted_confirmed: "", resolution_notes: "",
};

export default function SpecimenRejections() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", discrepancy_type: "", days: "", voided: "" });
  const [logOpen, setLogOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [resolve, setResolve] = useState({ ...EMPTY_RESOLVE });
  const [retireRow, setRetireRow] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const { data: res } = await api.get("/specimen-rejections", { params: p });
      setData(res);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const canEdit = data?.can_edit;
  const canRetire = ["admin", "supervisor"].includes(user?.role);
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const exportFile = async (kind) => {
    setBusy(kind);
    try {
      const res = await api.get(`/specimen-rejections/export.${kind}`, {
        params: { days: Number(filters.days) || 365, ...(filters.status ? { status: filters.status } : {}) },
        responseType: "blob",
      });
      download(res.data, `PathQity_SpecimenRejectionLog.${kind}`);
      toast.success(kind === "pdf" ? "Log PDF downloaded" : "CSV downloaded");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const submitResolve = async (e) => {
    e.preventDefault();
    setBusy("resolve");
    try {
      await api.post(`/specimen-rejections/${detail.id}/resolve`, resolve);
      toast.success("Discrepancy resolved — the case is released");
      setDetail(null);
      setResolve({ ...EMPTY_RESOLVE });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const reopen = async () => {
    const why = window.prompt("Why is this being reopened?");
    if (!why || why.trim().length < 5) return;
    try {
      await api.post(`/specimen-rejections/${detail.id}/reopen`, { reason: why });
      toast.success("Reopened");
      setDetail(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="rejections-page">
      <ModuleNav
        title="Specimen rejections & discrepancies"
        blurb="Every container that arrived unlabeled, mislabeled, leaking or wrong — what was found, who at the submitting site was called, and what they confirmed."
        actions={(
          <>
            <Button variant="outline" onClick={() => exportFile("csv")} disabled={busy === "csv"}
                    data-testid="rejections-export-csv" className="h-11 border-slate-300">
              <FileDown size={15} className="mr-2" /> CSV
            </Button>
            <Button variant="outline" onClick={() => exportFile("pdf")} disabled={busy === "pdf"}
                    data-testid="rejections-export-pdf" className="h-11 border-slate-300">
              <Printer size={15} className="mr-2" /> Print log
            </Button>
            {canEdit && (
              <Button onClick={() => setLogOpen(true)} data-testid="new-rejection-button"
                      className="h-11 bg-red-800 hover:bg-red-900">
                <Plus size={16} className="mr-2" /> Log a discrepancy
              </Button>
            )}
          </>
        )}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Tile label="Records shown" value={totals.total ?? 0} testid="rejections-tile-total" />
        <Tile label="Still open" value={totals.open ?? 0} testid="rejections-tile-open" tone="warn" />
        <Tile label="Holding a case" value={totals.held ?? 0} testid="rejections-tile-held" tone="bad" />
      </div>

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-[200px] flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Search</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} data-testid="rejections-search-input"
                   onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   placeholder="Log number, accession, patient, clinic or contact"
                   className="h-11 border-slate-300 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
          <select value={filters.status} data-testid="rejections-status-filter"
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Any status</option>
            <option value="Open">Open</option>
            <option value="Resolved">Resolved</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Discrepancy</Label>
          <select value={filters.discrepancy_type} data-testid="rejections-type-filter"
                  onChange={(e) => setFilters((f) => ({ ...f, discrepancy_type: e.target.value }))}
                  className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All types</option>
            {(data?.types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Period</Label>
          <select value={filters.days} data-testid="rejections-days-filter"
                  onChange={(e) => setFilters((f) => ({ ...f, days: e.target.value }))}
                  className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All time</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 365 days</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">View</Label>
          <select value={filters.voided} data-testid="rejections-voided-filter"
                  onChange={(e) => setFilters((f) => ({ ...f, voided: e.target.value }))}
                  className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Active records</option>
            <option value="true">Retired records</option>
          </select>
        </div>
        <Button variant="outline" data-testid="rejections-clear-filters" className="h-11 border-slate-300"
                onClick={() => setFilters({ q: "", status: "", discrepancy_type: "", days: "", voided: "" })}>
          Clear
        </Button>
      </div>

      <div className="border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm" data-testid="rejections-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2">Log no.</th><th className="px-3 py-2">Discovered</th>
                <th className="px-3 py-2">Specimen</th><th className="px-3 py-2">Discrepancy</th>
                <th className="px-3 py-2">From</th><th className="px-3 py-2">Resolution</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50"
                    data-testid={`rejection-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => { setDetail(r); setResolve({ ...EMPTY_RESOLVE }); }}
                            data-testid={`rejection-open-${r.id}`}
                            className="font-mono text-xs font-bold text-[#134e4a] hover:underline">
                      {r.rejection_number}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtDateTime(r.discovered_on)}</td>
                  <td className="px-3 py-2">
                    {r.case_id ? (
                      <Link to={`/cases/${r.case_id}`} className="font-mono text-xs font-semibold text-[#134e4a] hover:underline">
                        {r.accession_number}
                      </Link>
                    ) : <span className="font-mono text-xs text-slate-500">{r.accession_number || "not accessioned"}</span>}
                    <div className="text-xs text-slate-600">
                      {r.patient_name}{r.part ? ` · ${r.part}` : ""}
                    </div>
                  </td>
                  <td className="max-w-[260px] px-3 py-2 text-slate-700">{r.discrepancy_type}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {r.submitting_location || "—"}{r.submitted_by ? ` · ${r.submitted_by}` : ""}
                  </td>
                  <td className="max-w-[240px] px-3 py-2 text-xs text-slate-600">
                    {r.resolution_type || <span className="text-slate-400">pending</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${
                      r.status === "Resolved" ? "bg-[#134e4a] text-white" : "bg-red-800 text-white"}`}>
                      {r.status}
                    </span>
                    {r.holding && (
                      <span className="ml-1 inline-flex items-center gap-1 bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-900"
                            data-testid={`rejection-holding-${r.id}`}>
                        <Lock size={10} /> case held
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="px-3 py-10 text-center" data-testid="rejections-empty">
            <p className="text-sm text-slate-500">
              {Object.values(filters).some(Boolean)
                ? "No records match these filters."
                : "No specimen discrepancies logged yet — that is a good thing."}
            </p>
          </div>
        )}
      </div>

      <RejectionDialog open={logOpen} onOpenChange={setLogOpen} onLogged={load} />

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="rejection-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">{detail?.rejection_number}</DialogTitle>
            <DialogDescription>
              {detail?.discrepancy_type} · found by {detail?.discovered_by} on {detail ? fmtDateTime(detail.discovered_on) : ""}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <dl className="grid gap-x-4 gap-y-2 border border-slate-200 p-3 text-sm sm:grid-cols-2">
                <Row label="Specimen" value={`${detail.accession_number || "not accessioned"}${detail.part ? ` · ${detail.part}` : ""}`} />
                <Row label="Patient" value={detail.patient_name || "—"} />
                <Row label="Submitting location" value={detail.submitting_location || "—"} />
                <Row label="Submitted by" value={detail.submitted_by || "—"} />
                <div className="sm:col-span-2">
                  <dt className="text-xs uppercase tracking-wide text-slate-500">What happened</dt>
                  <dd className="text-slate-800" data-testid="rejection-detail-description">{detail.description}</dd>
                </div>
              </dl>

              {detail.status === "Resolved" ? (
                <div className="space-y-2 border-l-2 border-[#134e4a] bg-slate-50 p-3 text-sm"
                     data-testid="rejection-resolution-block">
                  <p className="font-semibold text-slate-900">{detail.resolution_type}</p>
                  <p className="text-slate-700">
                    {detail.contacted_name} ({detail.contacted_role}) contacted {fmtDateTime(detail.contacted_at)} —
                    confirmed: “{detail.contacted_confirmed}”
                  </p>
                  {detail.resolution_notes && <p className="text-slate-600">{detail.resolution_notes}</p>}
                  <p className="text-xs text-slate-500">
                    Closed by {detail.resolved_by} on {fmtDateTime(detail.resolved_at)}
                  </p>
                  {canRetire && (
                    <Button variant="outline" onClick={reopen} data-testid="rejection-reopen-button"
                            className="h-10 border-slate-300">
                      <Undo2 size={14} className="mr-2" /> Reopen
                    </Button>
                  )}
                </div>
              ) : canEdit ? (
                <form onSubmit={submitResolve} className="space-y-3 border border-slate-200 p-3"
                      data-testid="rejection-resolve-form">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Document the resolution
                  </p>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">How was it resolved *</Label>
                    <select value={resolve.resolution_type} required data-testid="resolve-type-select"
                            onChange={(e) => setResolve((r) => ({ ...r, resolution_type: e.target.value }))}
                            className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                      <option value="">Choose…</option>
                      {(data?.resolutions || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-slate-600">Who was contacted *</Label>
                      <Input value={resolve.contacted_name} required minLength={2} data-testid="resolve-contact-input"
                             onChange={(e) => setResolve((r) => ({ ...r, contacted_name: e.target.value }))}
                             className="mt-1 h-11 border-slate-300" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-slate-600">Their role *</Label>
                      <Input value={resolve.contacted_role} required minLength={2} data-testid="resolve-role-input"
                             placeholder="RN, MA, provider…" className="mt-1 h-11 border-slate-300"
                             onChange={(e) => setResolve((r) => ({ ...r, contacted_role: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-slate-600">Date &amp; time</Label>
                      <Input type="datetime-local" value={resolve.contacted_at} data-testid="resolve-at-input"
                             onChange={(e) => setResolve((r) => ({ ...r, contacted_at: e.target.value }))}
                             className="mt-1 h-11 border-slate-300" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">What they confirmed *</Label>
                    <Textarea value={resolve.contacted_confirmed} required minLength={3}
                              data-testid="resolve-confirmed-input" className="mt-1 min-h-[70px] border-slate-300"
                              placeholder="Read back to me: Jack Nicholson, DOB 10/03/1948, right apex — confirmed by the collecting RN"
                              onChange={(e) => setResolve((r) => ({ ...r, contacted_confirmed: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Notes (optional)</Label>
                    <Textarea value={resolve.resolution_notes} data-testid="resolve-notes-input"
                              onChange={(e) => setResolve((r) => ({ ...r, resolution_notes: e.target.value }))}
                              className="mt-1 min-h-[60px] border-slate-300" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="submit" disabled={busy === "resolve"} data-testid="resolve-submit"
                            className="h-11 flex-1 bg-[#134e4a] hover:bg-[#0f3f3c]">
                      {busy === "resolve" ? "Saving…" : "Resolve & release the case"}
                    </Button>
                    {canRetire && !detail.voided && (
                      <Button type="button" variant="outline" onClick={() => setRetireRow(detail)}
                              data-testid="rejection-retire-button"
                              className="h-11 border-red-300 text-red-800 hover:bg-red-50">
                        <Ban size={15} className="mr-2" /> Retire
                      </Button>
                    )}
                  </div>
                </form>
              ) : null}

              {detail.voided && (
                <div className="border-l-2 border-red-800 bg-red-50 p-3 text-sm text-red-900"
                     data-testid="rejection-retired-block">
                  Retired — {detail.void_reason} · {detail.voided_by} on {fmtDate(detail.voided_at)}
                  {canRetire && (
                    <Button variant="outline" data-testid="rejection-restore-button" className="ml-3 h-9"
                            onClick={async () => {
                              try {
                                await api.post(`/specimen-rejections/${detail.id}/restore`);
                                toast.success("Record reinstated");
                                setDetail(null);
                                load();
                              } catch (e) { toast.error(apiError(e)); }
                            }}>
                      Reinstate
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <RetireDialog
        open={!!retireRow} onOpenChange={(v) => !v && setRetireRow(null)} testid="retire-rejection-dialog"
        title={`Retire ${retireRow?.rejection_number || ""}`} confirmLabel="Retire record"
        description="Use this when a discrepancy was logged in error or duplicated. It stays on file under the Retired view and stops holding the case."
        onConfirm={async (reason) => {
          await api.post(`/specimen-rejections/${retireRow.id}/void`, { reason });
          toast.success("Record retired");
          setDetail(null);
          load();
        }}
      />
    </div>
  );
}

const Tile = ({ label, value, testid, tone }) => (
  <div className={`border p-3 ${tone === "bad" ? "border-red-200 bg-red-50"
    : tone === "warn" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-600">{label}</p>
    <p className="font-display text-2xl font-bold text-slate-900">{value}</p>
  </div>
);

const Row = ({ label, value }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
    <dd className="text-slate-800">{value}</dd>
  </div>
);
